<template>
    <div id="transformation-segments-manager" class="mx-2">
        <div class="row mt-3">
            <div class="col-12">
                <h1>Audience Templates</h1>
                <p>
                    Manage the default audiences here including updating existing audiences.
                </p>
            </div>
        </div>

        <div class="card mb-3 table-card">
            <div class="card-header">
                <div class="row">
                    <div class="col-md-6 col-12">
                        <h3 class="mt-2">Audiences ({{ filtered_segment_list.length }})</h3>
                    </div>
                    <div class="col-md-6 d-none d-md-block">
                        <div class="d-flex justify-content-end justify-self-end gap-2">
                            <div class="position-relative">
                                <input 
                                    type="text" 
                                    class="search-box form-control me-1 py-2" 
                                    placeholder="Search by Audience Name..." 
                                    aria-label="Search by Audience Name..." 
                                    v-model="search_key" 
                                    @input="handleFilterSelection"
                                >
                                <i class="fa fa-search search-placeholder-icon" v-show="!search_key"></i>
                            </div>
                            <a class="btn btn-primary" href="/admin/transformation_segments/create">
                                <img class="icon" src="/img/icons/avid-icons/plus.svg" alt="Add icon">Add an Audience
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card-body p-0">
                <div class="table-width-responsive">  
                    <table class="table table-with-pagination mb-0">
                        <thead>
                            <tr class="table-header-row">
                                <th @click="sortByKey('name')" class="d-table-cell">
                                    <div class="d-flex flex-row">
                                        Audience
                                        <div class="d-flex flex-column">
                                            <img 
                                                class="sort-key" 
                                                src="/img/icons/avid-icons/chevron-up.svg" 
                                                :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'asc' }"
                                                alt="Sort ascending"
                                            >
                                            <img 
                                                class="sort-key" 
                                                src="/img/icons/avid-icons/chevron-down.svg" 
                                                :class="{ 'sort-key-active': sort_key === 'name' && sort_order === 'des' }"
                                                alt="Sort descending"
                                            >
                                        </div>
                                    </div>
                                </th>
                                <th class="d-none d-md-table-cell">Source</th>
                                <th class="d-none d-md-table-cell">Type</th>
                                <th class="d-table-cell column-sort-disabled pe-3" width="1">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="segment in list_shown" :key="segment.id">
                                <td class="d-table-cell">
                                    <a class="text-wrap" :href="'/admin/transformation_segments/' + segment.id + '/edit'">
                                        {{ segment.name }}
                                    </a>
                                </td>
                                <td class="d-none d-md-table-cell">
                                    <template v-if="segment.source === 'transformation'">
                                        Data Set: {{ getTransformationName(segment.data_source_id) }}
                                    </template>
                                    <template v-else>
                                        {{ propercase(segment.source) }}
                                    </template>
                                </td>
                                <td class="d-none d-md-table-cell">
                                    <span class="badge badge-secondary">{{ propercase(segment.type || 'owned') }}</span>
                                </td>
                                <td class="d-table-cell">
                                    <div class="dropdown d-flex justify-content-center">
                                        <button 
                                            class="btn btn-white btn-icon-only-md" 
                                            data-bs-toggle="dropdown" 
                                            data-bs-auto-close="true" 
                                            aria-expanded="false"
                                         aria-label="Button">
                                            <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" alt="More options" data-bs-toggle="tooltip" title="Actions">
                                        </button>
                                        <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                            <a class="dropdown-item" :href="'/admin/transformation_segments/' + segment.id + '/edit'">
                                                <img 
                                                    class="icon" 
                                                    src="/img/icons/avid-icons/edit.svg" 
                                                    data-bs-toggle="tooltip" 
                                                    data-bs-placement="bottom" 
                                                    title="Edit Audience"
                                                    alt="Edit icon"
                                                >
                                                Edit Audience
                                            </a>
                                            <div class="dropdown-divider"></div>
                                            <a class="dropdown-item text-danger" href="#" @click.prevent="deleteSegment(segment)">
                                                <img 
                                                    class="icon red-icon" 
                                                    src="/img/icons/avid-icons/trash.svg" 
                                                    data-bs-toggle="tooltip" 
                                                    data-bs-placement="bottom" 
                                                    title="Delete Audience"
                                                    alt="Delete icon"
                                                >
                                                Delete Audience
                                            </a>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot class="table-border-bottom-0">
                            <tr>
                                <td colspan="100%" class="py-0">
                                    <div class="table-pagination">
                                        <div class="table-pagination--group d-none d-md-flex">
                                            <div class="position-relative">
                                                <select 
                                                    class="form-select" 
                                                    name="num_per_page" 
                                                    id="num_per_page" 
                                                    v-model="num_per_page" 
                                                    @change="reloadPagination(filtered_segment_list)"
                                                 aria-label="Num Per Page">
                                                    <option value="5000">ALL</option>
                                                    <option 
                                                        v-for="num in num_per_page_options" 
                                                        :key="num" 
                                                        :value="num"
                                                    >
                                                        {{ num }}
                                                    </option>
                                                </select>
                                                <div class="select-display-text">
                                                    Showing {{ num_per_page === '5000' ? 'ALL' : num_per_page }}
                                                </div>
                                            </div>
                                        </div>

                                        <div class="table-pagination--group d-none d-lg-flex">
                                            {{ filtered_segment_list.length === 0 ? '0' : item_interval }} of {{ filtered_segment_list.length }}
                                        </div>

                                        <div class="table-pagination--group">
                                            <button 
                                                v-if="page_count > 1" 
                                                class="btn btn-secondary btn-icon-only-md me-1" 
                                                @click="moveToPrevPage(filtered_segment_list)" 
                                                :disabled="page_index === 0"
                                                data-bs-toggle="tooltip"
                                                title="Previous page"
                                            >
                                                <img class="icon" src="/img/icons/avid-icons/chevron-left.svg" alt="Previous page">
                                            </button>

                                            <div class="d-flex page-selector" v-if="page_index_array.length <= 3">
                                                <button 
                                                    v-for="page in page_index_array" 
                                                    :key="page" 
                                                    class="btn btn-none btn-icon-only-md" 
                                                    :class="{ 'current-page': page === page_index + 1 }" 
                                                    @click="moveToPage(page, filtered_segment_list)"
                                                >
                                                    {{ page }}
                                                </button>
                                            </div>
                                            <div class="d-flex page-selector" v-else>
                                                <button 
                                                    v-for="page in page_index_array.slice(0,2)" 
                                                    :key="page" 
                                                    class="btn btn-none btn-icon-only-md" 
                                                    :class="{ 'current-page': page === page_index + 1 }" 
                                                    @click="moveToPage(page, filtered_segment_list)"
                                                >
                                                    {{ page }}
                                                </button>

                                                <button 
                                                    v-if="page_index > 2" 
                                                    class="btn btn-none btn-icon-only-md text-nowrap" 
                                                    disabled
                                                >
                                                    ...
                                                </button>
                                                <button 
                                                    v-if="page_index >= 2 && page_index < page_index_array.length - 1" 
                                                    class="btn btn-none btn-icon-only-md current-page"
                                                >
                                                    {{ page_index + 1 }}
                                                </button>

                                                <button 
                                                    v-if="page_index < page_index_array.length - 2" 
                                                    class="btn btn-none btn-icon-only-md text-nowrap" 
                                                    disabled
                                                 aria-hidden="true">
                                                    ...
                                                </button>
                                                <button 
                                                    :class="{ 'current-page': page_index === page_index_array.length - 1 }" 
                                                    class="btn btn-none btn-icon-only-md" 
                                                    @click="moveToPage(page_index_array.length, filtered_segment_list)" 
                                                    :key="page_index_array.length"
                                                >
                                                    {{ page_index_array.length }}
                                                </button>
                                            </div>

                                            <button 
                                                v-if="page_count > 1" 
                                                class="btn btn-secondary btn-icon-only-md ms-1" 
                                                @click="moveToNextPage(filtered_segment_list)" 
                                                :disabled="page_index === page_count - 1"
                                                data-bs-toggle="tooltip"
                                                title="Next page"
                                            >
                                                <img class="icon" src="/img/icons/avid-icons/chevron-right.svg" alt="Next page">
                                            </button>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>                    
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import tablePaginationMixin from '../../mixins/table_pagination';
import Swal from 'sweetalert2';

export default defineComponent({
    name: 'TransformationSegmentsManager',
    mixins: [tablePaginationMixin],
    props: {
        segments: {
            type: Array,
            required: true
        },
        transformations: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            filtered_segment_list: [],
            list_shown: [],
            search_key: '',
            sort_key: null,
            sort_order: 'asc',
        };
    },
    beforeMount() {
        this.filtered_segment_list = this.segments;
        this.reloadPagination(this.filtered_segment_list);
    },
    mounted() {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

        for (let i = 0; i < tooltipTriggerList.length; i++) {
            (function(index) {
                tooltipTriggerList[index].addEventListener('click', function() {
                    tooltipList[index].hide();
                });
                tooltipTriggerList[index].addEventListener('mouseleave', function() {
                    tooltipList[index].hide();
                });
            })(i);
        }
    },
    methods: {
        propercase(str) {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        },
        getTransformationName(dataSourceId) {
            if (!dataSourceId || !this.transformations) return '';
            const transformation = this.transformations.find(t => t.id === dataSourceId);
            return transformation ? transformation.name : '';
        },
        getSource(source) {
            if (source === 'constituent') {
                return 'Constituent';
            } else if (source === 'subscriber') {
                return 'Subscriber';
            }
            return "Data Set";
        },
        handleFilterSelection() {
            if (!this.search_key) {
                this.filtered_segment_list = [...this.segments];
            } else {
                this.filtered_segment_list = [...this.segments].filter(seg => {
                    return seg.name.toLowerCase().includes(this.search_key.toLowerCase());
                });
            }

            this.reloadPagination(this.filtered_segment_list);
            this.$forceUpdate();
        },
        sortByKey(key) {
            if (this.sort_key !== key) {
                this.sort_order = 'asc';
            } else {
                this.sort_order = this.sort_order === 'asc' ? 'des' : 'asc';
            }
            this.sort_key = key;
            this.sortDataWithLabel();
        },
        sortDataWithLabel() {
            this.filtered_segment_list.sort((a, b) => {
                if (this.sort_order === 'asc') {
                    return a[this.sort_key] > b[this.sort_key] ? 1 : -1;
                }
                return a[this.sort_key] < b[this.sort_key] ? 1 : -1;
            });

            this.reloadPagination(this.filtered_segment_list);
        },
        deleteSegment(segment) {
            Swal.fire({
                title: "Are you sure you want to delete this audience?",
                text: "This action cannot be undone.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Yes, delete",
                customClass: {
                    popup: 'swal-warning'
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    window.axios.delete('/api/transformation-segments/' + segment.id)
                        .then(response => {
                            if (response.status === 200) {
                                const index = this.filtered_segment_list.findIndex(s => s.id === segment.id);
                                this.filtered_segment_list.splice(index, 1);
                                this.reloadPagination(this.filtered_segment_list);

                                Swal.fire({
                                    title: "Deleted!",
                                    text: "This audience has been removed.",
                                    icon: "success",
                                    customClass: {
                                        popup: 'swal-success'
                                    },
                                });
                            }
                        });
                }
            });
        }
    }
});
</script>